import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { activityAPI, bustCache } from '../utils/api';
import { formatChangeValue } from '../utils/html';
import { prettyFieldLabel } from '../utils/labels';

/* ─────────────────────────────────────────────────────────────────────────────
   HistoryPanel — slide-in drawer showing activity timeline for an entity.
   Now theme-aware via ThemeContext tokens.

   Props unchanged:
     entityId   {string}   — the requirement or candidate ID
     entityName {string}   — display title in the panel header
     onClose    {function} — called when user closes the panel

   Action tinted chips keep their semantic color (emerald/amber/red/indigo/cyan)
   because semantic states need to read the same on both themes. The tint
   opacity (.15 bg / .30 border) works on both dark and light — chromatic
   text color stays legible on near-white because the text is a saturated
   400-level and the bg is a tinted fill, not transparent-on-chrome.
   ───────────────────────────────────────────────────────────────────────────── */

const ACTION_META = {
  CREATE_REQUIREMENT:  { label: 'Created',            color: 'text-emerald-500 dark:text-emerald-400', bg: 'bg-emerald-400/15 border-emerald-400/30', dot: 'bg-emerald-400' },
  UPDATE_REQUIREMENT:  { label: 'Updated',            color: 'text-indigo-500  dark:text-indigo-400',  bg: 'bg-indigo-400/15 border-indigo-400/30',   dot: 'bg-indigo-400'  },
  DELETE_REQUIREMENT:  { label: 'Deleted',            color: 'text-red-500     dark:text-red-400',     bg: 'bg-red-400/15 border-red-400/30',          dot: 'bg-red-400'     },
  CREATE_CANDIDATE:    { label: 'Created',            color: 'text-emerald-500 dark:text-emerald-400', bg: 'bg-emerald-400/15 border-emerald-400/30', dot: 'bg-emerald-400' },
  UPDATE_CANDIDATE:    { label: 'Updated',            color: 'text-indigo-500  dark:text-indigo-400',  bg: 'bg-indigo-400/15 border-indigo-400/30',   dot: 'bg-indigo-400'  },
  DELETE_CANDIDATE:    { label: 'Deleted',            color: 'text-red-500     dark:text-red-400',     bg: 'bg-red-400/15 border-red-400/30',          dot: 'bg-red-400'     },
  STATUS_CHANGE:       { label: 'Status Changed',     color: 'text-amber-600   dark:text-amber-400',   bg: 'bg-amber-400/15 border-amber-400/30',      dot: 'bg-amber-400'   },
  UPDATE_STATUS:       { label: 'Status Changed',     color: 'text-amber-600   dark:text-amber-400',   bg: 'bg-amber-400/15 border-amber-400/30',      dot: 'bg-amber-400'   },
  ASSIGN_RECRUITER:    { label: 'Recruiter Assigned', color: 'text-violet-500  dark:text-violet-400',  bg: 'bg-violet-400/15 border-violet-400/30',   dot: 'bg-violet-400'  },
  UPLOAD_RESUME:       { label: 'Resume Uploaded',    color: 'text-cyan-600    dark:text-cyan-400',    bg: 'bg-cyan-400/15 border-cyan-400/30',        dot: 'bg-cyan-400'    },
  SEND_EMAIL:          { label: 'Email Sent',         color: 'text-sky-600     dark:text-sky-400',     bg: 'bg-sky-400/15 border-sky-400/30',          dot: 'bg-sky-400'     },
};

function getMeta(action = '') {
  if (ACTION_META[action]) return ACTION_META[action];
  if (action.startsWith('CREATE')) return ACTION_META.CREATE_REQUIREMENT;
  if (action.startsWith('UPDATE')) return ACTION_META.UPDATE_REQUIREMENT;
  if (action.startsWith('DELETE')) return ACTION_META.DELETE_REQUIREMENT;
  return {
    label: action.replace(/_/g, ' '),
    color: 'text-[var(--text-tertiary)]',
    bg: 'bg-[var(--surface-3)] border-[var(--border-subtle)]',
    dot: 'bg-slate-400',
  };
}

/* ─── Role badge ─────────────────────────────────────────────────────────── */
function RoleBadge({ role }) {
  const cls = {
    SuperAdmin:       'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
    RecruiterManager: 'bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30',
    Recruiter:        'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  }[role] || 'bg-[var(--surface-3)] text-[var(--text-tertiary)] border-[var(--border-subtle)]';
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      {role}
    </span>
  );
}

/* ─── Field change row ───────────────────────────────────────────────────── */
function FieldChange({ field, before, after }) {
  // Renders any value readably — HTML→text, arrays/objects→summary, never
  // "[object Object]". See formatChangeValue.
  const fmt = (v) => {
    const text = formatChangeValue(v);
    return text || <span className="italic" style={{ color: 'var(--text-faint)' }}>—</span>;
  };
  return (
    <div className="flex items-baseline gap-2 text-xs py-0.5">
      <span
        className="min-w-[90px] shrink-0 capitalize"
        style={{ color: 'var(--text-muted)' }}
      >
        {prettyFieldLabel(field)}
      </span>
      {/* min-w-0 + break-words let long values (RTE text, UUIDs) wrap inside the
          drawer instead of overflowing past its edge. */}
      <span className="min-w-0 flex-1" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        <span className="text-red-500/85 dark:text-red-300/80 line-through">{fmt(before)}</span>
        <span style={{ color: 'var(--text-muted)' }}> → </span>
        <span className="text-emerald-600 dark:text-emerald-300/90">{fmt(after)}</span>
      </span>
    </div>
  );
}

/* ─── Single timeline entry ──────────────────────────────────────────────── */
function TimelineEntry({ log }) {
  const [expanded, setExpanded] = useState(false);
  const meta       = getMeta(log.action);
  const changes    = log.details?.changes || {};
  const hasChanges = Object.keys(changes).length > 0;

  const relTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7)  return `${d}d ago`;
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const absTime = new Date(log.createdAt).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="relative pl-7">
      {/* Timeline dot — outlined by the drawer bg so it punches out of the line */}
      <span
        className={`absolute left-0 top-2 w-3 h-3 rounded-full border-2 ${meta.dot}`}
        style={{ borderColor: 'var(--surface-2)' }}
      />

      <div className={`rounded-xl border px-3 py-2.5 mb-0.5 transition-all ${meta.bg}`}>
        {/* Header */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span
            className={`font-semibold text-sm min-w-0 break-words ${meta.color}`}
            style={{ overflowWrap: 'anywhere' }}
          >
            {meta.label}
          </span>
          <span
            className="text-xs shrink-0"
            title={absTime}
            style={{ color: 'var(--text-muted)' }}
          >
            {relTime(log.createdAt)}
          </span>
        </div>

        {/* User info */}
        <div className="flex items-center gap-2 mt-1 min-w-0">
          <div className="w-5 h-5 rounded-full bg-indigo-600/60 flex items-center justify-center text-[10px] text-white font-bold shrink-0">
            {log.user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <span
            className="text-xs font-medium min-w-0 break-words"
            style={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}
          >
            {log.user?.name || 'Unknown'}
          </span>
          {log.user?.role && <RoleBadge role={log.user.role} />}
        </div>

        {/* Notes */}
        {log.details?.note && (
          <p className="text-xs mt-1.5 italic" style={{ color: 'var(--text-tertiary)' }}>
            {log.details.note}
          </p>
        )}

        {/* Changes toggle */}
        {hasChanges && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-2 flex items-center gap-1 text-xs transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}
          >
            <span className="text-[10px]">{expanded ? '▼' : '▶'}</span>
            {Object.keys(changes).length} field{Object.keys(changes).length !== 1 ? 's' : ''} changed
          </button>
        )}
        {hasChanges && expanded && (
          <div
            className="mt-2 pt-2 space-y-0.5"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            {Object.entries(changes).map(([field, val]) => (
              <FieldChange
                key={field}
                field={field}
                before={val?.before ?? val?.old}
                after={val?.after ?? val?.new}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main HistoryPanel component ────────────────────────────────────────── */
export default function HistoryPanel({ entityId, entityName, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async (fresh = false) => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      // Explicit refresh bypasses api.js's 30s GET cache — otherwise it re-reads
      // the same cached entity history and the refresh appears to do nothing.
      if (fresh) bustCache(`/activity-logs/entity/${entityId}`);
      const { data } = await activityAPI.getByEntity(entityId);
      setLogs(data.logs || []);
    } catch {
      setError('Could not load history.');
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        width: '100%',
        maxWidth: 420,
        height: '100vh',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-flat)',
        borderLeft: '1px solid var(--border-brand)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
        animation: 'slideInLeft 0.25s ease',
      }}
    >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'var(--accent-bg)',
                border: '1px solid var(--border-brand)',
                boxShadow: 'var(--shadow-glow)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ color: 'var(--accent-text)' }}>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                Activity History
              </h2>
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                {entityName || 'Entity'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(true)}
              title="Refresh"
              disabled={loading}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.background = 'var(--hover-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-tertiary)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: loading ? 'spin 0.7s linear infinite' : 'none' }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all text-lg leading-none"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.background = 'var(--hover-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-tertiary)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Summary badge */}
        {!loading && !error && (
          <div
            className="px-5 py-2 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border-subtle)' }}
          >
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
              style={{
                background: 'var(--accent-bg)',
                color: 'var(--accent-text)',
                border: '1px solid var(--border-brand)',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="3" />
              </svg>
              {logs.length} event{logs.length !== 1 ? 's' : ''} recorded
            </span>
          </div>
        )}

        {/* Timeline content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 custom-scroll">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div
                className="w-8 h-8 rounded-full border-2 border-t-transparent"
                style={{
                  borderColor: 'var(--border-brand)',
                  borderTopColor: 'var(--brand-primary)',
                  animation: 'spin 0.7s linear infinite',
                }}
              />
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading history…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
              <span className="text-3xl">⚠️</span>
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{error}</p>
              <button
                onClick={load}
                className="text-sm font-medium transition-colors"
                style={{ color: 'var(--accent-text)' }}
              >
                Try again
              </button>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'var(--accent-bg)',
                  border: '1px solid var(--border-brand)',
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ color: 'var(--brand-primary)' }}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No history yet
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Changes will appear here as they're made
                </p>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div
                className="absolute left-[5px] top-4 bottom-4 w-px"
                style={{
                  background: 'linear-gradient(to bottom, var(--border-brand), transparent)',
                }}
              />
              <div className="space-y-3">
                {logs.map((log) => (
                  <TimelineEntry key={log.id} log={log} />
                ))}
              </div>

              {/* End cap */}
              <div className="mt-4 pl-7">
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                  All activity recorded
                </p>
              </div>
            </div>
          )}
        </div>
    </div>,
    document.body
  );
}